<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h2 class="mb-4">Quản lý Đồng bộ Dữ liệu (ETL)</h2>
      </v-col>
    </v-row>

    <v-row>
      <!-- Card 1: Status Dashboard -->
      <v-col cols="12" md="4">
        <v-card class="elevation-1 h-100 d-flex flex-column">
          <v-card-title class="headline d-flex align-center">
            Trạng thái luồng
            <v-spacer></v-spacer>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click="() => fetchStatus()">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>Làm mới trạng thái</span>
            </v-tooltip>
          </v-card-title>

          <v-card-text>
            <div class="d-flex align-center mb-5">
              <strong class="mr-2">Trạng thái hiện tại:</strong>
              <v-chip
                :color="loadingStatus ? 'grey' : statusColor"
                text-color="white"
                class="font-weight-bold px-3"
                small
              >
                <v-icon left small v-if="loadingStatus" class="mdi-spin">mdi-loading</v-icon>
                {{ loadingStatus ? 'Đang tải...' : statusText }}
              </v-chip>
            </div>

            <div class="action-buttons-group">
              <!-- Nút Resume khi paused -->
              <v-btn 
                v-if="status.status === 'paused'"
                color="success" 
                block
                large
                depressed
                class="mb-3"
                @click="resumeJob" 
                :loading="actionLoading"
              >
                <v-icon left>mdi-play</v-icon>
                Tiếp Tục Luồng
              </v-btn>

              <!-- Nút Pause khi running -->
              <v-btn 
                v-else-if="status.status === 'running'"
                color="error" 
                block
                large
                depressed
                class="mb-3"
                @click="pauseJob" 
                :loading="actionLoading"
              >
                <v-icon left>mdi-pause</v-icon>
                Tạm Dừng Luồng
              </v-btn>

              <!-- Trạng thái khác -->
              <v-btn 
                v-else
                color="success" 
                block
                large
                depressed
                class="mb-3"
                @click="resumeJob" 
                :loading="actionLoading"
              >
                <v-icon left>mdi-play</v-icon>
                Tiếp Tục Luồng
              </v-btn>

              <!-- Chạy thủ công 1 lần -->
              <v-btn 
                color="primary" 
                outlined
                block
                large
                @click="runJob" 
                :loading="actionLoading"
              >
                <v-icon left>mdi-lightning-bolt</v-icon>
                Chạy Ngay 1 Lần
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Card 2: Database Config -->
      <v-col cols="12" md="8">
        <v-card class="elevation-1 h-100 d-flex flex-column" :loading="loadingConfig">
          <v-card-title class="headline">
            Cấu hình Kết nối CSDL
          </v-card-title>
          <v-card-text class="flex-grow-1">
            <v-form ref="configForm">
              <v-row>
                <v-col cols="12" sm="6">
                  <div class="subtitle-1 font-weight-bold mb-2 primary--text">Nguồn (MongoDB)</div>
                  <v-text-field
                    v-model="configData.mongo_source_database"
                    label="Tên Database (Mongo)"
                    outlined
                    dense
                  ></v-text-field>
                  <v-text-field
                    v-model="configData.mongo_source_collection"
                    label="Tên Collection (Mongo)"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12" sm="6">
                  <div class="subtitle-1 font-weight-bold mb-2 primary--text">Đích (PostgreSQL)</div>
                  <v-text-field
                    v-model="configData.psql_target_database"
                    label="Tên Database (PSQL)"
                    outlined
                    dense
                  ></v-text-field>
                  <v-text-field
                    v-model="configData.psql_target_table"
                    label="Tên Bảng (PSQL)"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="saveConfig" :loading="savingConfig">
              <v-icon left>mdi-content-save</v-icon>
              Lưu Cấu Hình
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <!-- Card 3: Scheduler Config -->
      <v-col cols="12">
        <v-card class="elevation-1" :loading="loadingStatus">
          <v-card-title class="headline">
            Tần suất Đồng bộ (Scheduler)
          </v-card-title>
          <v-card-text>
            <v-form ref="schedulerForm">
              <v-radio-group v-model="status.update_interval_type" row>
                <v-radio label="Hàng giờ (Hourly)" value="hourly"></v-radio>
                <v-radio label="Hàng ngày (Daily)" value="daily"></v-radio>
              </v-radio-group>
              
              <v-slide-y-transition>
                <div v-if="status.update_interval_type === 'daily'" class="mt-2" style="max-width: 300px;">
                  <v-text-field
                    v-model.number="status.update_interval_value"
                    label="Giờ chạy (0 - 23)"
                    type="number"
                    min="0"
                    max="23"
                    outlined
                    dense
                    :rules="[
                      v => v !== null && v !== '' || 'Vui lòng nhập giờ',
                      v => (v >= 0 && v <= 23) || 'Giờ phải từ 0 đến 23'
                    ]"
                    hint="Ví dụ: Nhập 2 để chạy lúc 2h sáng"
                    persistent-hint
                  ></v-text-field>
                </div>
              </v-slide-y-transition>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="saveScheduler" :loading="savingScheduler">
              <v-icon left>mdi-clock-outline</v-icon>
              Lưu Chu Kỳ
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { etlService } from '@src/service/http/etlService'
import { ConfirmBuilder } from '@utils'

export default {
  name: 'EtlManager',
  data() {
    return {
      status: {
        status: 'unknown',
        update_interval_type: 'hourly',
        update_interval_value: null
      },
      configData: {
        mongo_source_database: '',
        mongo_source_collection: '',
        psql_target_database: '',
        psql_target_table: ''
      },
      originalConfig: {},
      loadingStatus: false,
      loadingConfig: false,
      actionLoading: false,
      savingConfig: false,
      savingScheduler: false
    }
  },
  computed: {
    statusColor() {
      if (this.status.status === 'running') return 'success'
      if (this.status.status === 'paused' || this.status.status === 'stopped') return 'warning'
      return 'grey'
    },
    statusText() {
      if (this.status.status === 'running') return 'Đang chạy'
      if (this.status.status === 'paused') return 'Đang tạm dừng'
      if (this.status.status === 'stopped') return 'Đã dừng'
      if (this.status.status === 'unknown') return 'Không rõ'
      return this.status.status || 'Chưa kết nối'
    }
  },
  mounted() {
    this.fetchStatus()
    this.fetchConfig()
  },
  methods: {
    async fetchStatus(silent = false) {
      if (!silent) this.loadingStatus = true
      try {
        const { data } = await etlService.getStatus()
        if (data && data.status === 'success' && data.data) {
          this.status = data.data
        }
      } catch (e) {
        console.error('Lỗi lấy trạng thái ETL:', e)
        if (!silent) {
          this.$store.dispatch('notify/error', { message: 'Không thể kết nối đến máy chủ ETL' })
        }
      } finally {
        if (!silent) this.loadingStatus = false
      }
    },
    
    async fetchConfig() {
      this.loadingConfig = true
      try {
        const { data } = await etlService.getConfig()
        if (data && data.status === 'success' && data.data) {
          this.configData = { ...this.configData, ...data.data }
          this.originalConfig = { ...this.configData }
        }
      } catch (e) {
        console.error('Lỗi lấy cấu hình ETL:', e)
        this.$store.dispatch('notify/error', { message: 'Không thể lấy cấu hình máy chủ ETL' })
      } finally {
        this.loadingConfig = false
      }
    },
    
    async saveConfig() {
      this.savingConfig = true
      try {
        // Chỉ gửi những trường có sự thay đổi
        const payload = {}
        for (const key in this.configData) {
          if (this.configData[key] !== this.originalConfig[key] && this.configData[key] !== '') {
            payload[key] = this.configData[key]
          }
        }
        
        if (Object.keys(payload).length === 0) {
          this.$store.dispatch('notify/info', { message: 'Không có thay đổi nào để lưu' })
          this.savingConfig = false
          return
        }
        
        const { data } = await etlService.updateConfig(payload)
        if (data && data.status === 'success') {
          this.$store.dispatch('notify/success', { message: 'Cập nhật cấu hình thành công' })
          this.originalConfig = { ...this.configData }
        }
      } catch (e) {
        this.$store.dispatch('notify/error', { message: 'Cập nhật cấu hình thất bại' })
      } finally {
        this.savingConfig = false
      }
    },
    
    async saveScheduler() {
      if (this.status.update_interval_type === 'daily' && !this.$refs.schedulerForm.validate()) {
        return
      }
      
      this.savingScheduler = true
      try {
        const payload = {
          update_interval_type: this.status.update_interval_type
        }
        if (this.status.update_interval_type === 'daily') {
          payload.update_interval_value = this.status.update_interval_value
        }
        
        const { data } = await etlService.modifyInterval(payload)
        if (data && data.status === 'success') {
          this.$store.dispatch('notify/success', { message: 'Cập nhật chu kỳ đồng bộ thành công' })
        }
      } catch (e) {
        this.$store.dispatch('notify/error', { message: 'Cập nhật chu kỳ thất bại' })
      } finally {
        this.savingScheduler = false
      }
    },
    
    async runJob() {
      this.actionLoading = true
      try {
        const { data } = await etlService.runJob()
        if (data && data.status === 'success') {
          this.$store.dispatch('notify/success', { message: 'Đã yêu cầu chạy đồng bộ (1 lần)' })
          this.fetchStatus()
        }
      } catch (e) {
        this.$store.dispatch('notify/error', { message: 'Không thể kích hoạt chạy đồng bộ' })
      } finally {
        this.actionLoading = false
      }
    },
    
    async resumeJob() {
      this.actionLoading = true
      try {
        const { data } = await etlService.resumeJob()
        if (data && data.status === 'success') {
          this.$store.dispatch('notify/success', { message: 'Đã tiếp tục luồng đồng bộ' })
          this.fetchStatus()
        }
      } catch (e) {
        this.$store.dispatch('notify/error', { message: 'Không thể tiếp tục luồng đồng bộ' })
      } finally {
        this.actionLoading = false
      }
    },
    
    async pauseJob() {
      let confirm = new ConfirmBuilder()
      confirm
        .addMessage('Bạn có chắc chắn muốn tạm dừng luồng đồng bộ hiện tại?')
        .setTitle('Xác nhận Tạm dừng')
        .setType('warning')

      if (await confirm.confirm()) {
        this.actionLoading = true
        try {
          const { data } = await etlService.pauseJob()
          if (data && data.status === 'success') {
            this.$store.dispatch('notify/success', { message: 'Đã tạm dừng luồng đồng bộ' })
            this.fetchStatus()
          }
        } catch (e) {
          this.$store.dispatch('notify/error', { message: 'Tạm dừng thất bại' })
        } finally {
          this.actionLoading = false
        }
      }
    }
  }
}
</script>

<style scoped>
.h-100 {
  height: 100%;
}
</style>
